/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PopupComponent } from '../popup/popup.component';
import { LeadStatusValues } from '../../../pages/leads/models/lead-status';
import { SearchService } from '../../../pages/search/services/search.service';
import {
  LEAD_ATTRIBUTE_CONFIG,
  LeadAttributeConfig,
} from '../../models/lead-info';
import { takeUntil } from 'rxjs';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { CommonService } from '../../services/common/common.service';
import { ToastType } from '../../interfaces/common';

enum StatusWarningType {
  None = 0,
  ActiveToOther = 1,
  BetweenFundedAndTA = 2,
}

@Component({
  selector: 'app-record-lead-info',
  templateUrl: './record-lead-info.component.html',
  styleUrls: ['./record-lead-info.component.scss'],
  standalone: true,
  imports: [PopupComponent, CommonModule],
})
export class RecordLeadInfoComponent extends DestroyBase implements OnInit {
  private searchService = inject(SearchService);
  private commonService = inject(CommonService);
  @Input() recordId!: string;
  @Input() leadAttributes: Record<string, any> | null = null;
  @Input() isEditClicked = false;
  @Input() crmStatus = false;
  @Input() source = '';

  @Output() leadStatusUpdated = new EventEmitter<{
    uniqueId: string;
    newStatus: string;
  }>();

  originalLeadAttributes: Record<string, any> | null = null;

  readonly leadConfig = LEAD_ATTRIBUTE_CONFIG;

  private readonly activeClientStatus = Number(
    LeadStatusValues['active_client'],
  );
  private readonly fundedOrTaStatuses = [
    Number(LeadStatusValues['active_funded']),
    Number(LeadStatusValues['active_funded_grant']),
    Number(LeadStatusValues['active_technical_assistance']),
  ];

  showStatusChangePopup = false;
  statusWarningHeading = 'Confirm Status Change';
  statusWarningSubtext = '';
  previousStatusLabel = '';
  newStatusLabel = '';

  private pendingStatusChange: {
    property: string;
    newStatus: number | null;
    previousStatus: number | null;
    selectElement?: HTMLSelectElement;
  } | null = null;

  get orderedAttributes(): LeadAttributeConfig[] {
    return this.leadConfig;
  }

  ngOnInit(): void {
    this.originalLeadAttributes = { ...this.leadAttributes };
    if (this.leadAttributes?.['lead_status'] != null) {
      const leadStatusIndex = this.leadConfig.findIndex(
        (item) => item.property === 'lead_status',
      );

      const options = this.leadConfig[leadStatusIndex]?.options;

      if (options) {
        this.leadConfig[leadStatusIndex].options = Object.fromEntries(
          Object.entries(options).filter(([, value]) => value !== 0),
        );
      }
    }
  }

  getDisplayValue(attribute: LeadAttributeConfig): string {
    const value = this.leadAttributes?.[attribute.property];

    if (
      attribute.type === 'dropdown' &&
      attribute.options &&
      value !== null &&
      value !== undefined
    ) {
      const option = Object.entries(attribute.options).find(
        ([, optionValue]) => optionValue === value,
      );

      return option?.[0] ?? '-';
    }

    if (
      attribute.type === 'number' &&
      attribute.prefix &&
      value !== null &&
      value !== undefined
    ) {
      return `${attribute.prefix}${value}`;
    }

    return value ?? '-';
  }

  getOptions(
    options?: Record<string, number>,
  ): { label: string; value: number }[] {
    if (!options) {
      return [];
    }

    const result = Object.entries(options).map(([label, value]) => ({
      label,
      value,
    }));

    return result;
  }

  getStatusLabel(statusValue: number | null | undefined): string {
    if (
      statusValue === null ||
      statusValue === undefined ||
      statusValue === 0
    ) {
      return 'Not defined';
    }

    const leadStatusConfig = this.leadConfig.find(
      (c) => c.property === 'lead_status',
    );

    if (leadStatusConfig?.options) {
      const entry = Object.entries(leadStatusConfig.options).find(
        ([, val]) => val === statusValue,
      );
      if (entry) {
        return entry[0];
      }
    }

    return String(statusValue);
  }

  getAttributeLabel(attribute: LeadAttributeConfig): string {
    return attribute.property_name;
  }

  // VLF lead whose saved (not in-progress) status is Active Funded
  isActiveFund(): boolean {
    if (this.source?.toLowerCase() !== 'vlf') {
      return false;
    }

    const savedStatus = this.originalLeadAttributes?.['lead_status'];

    return (
      savedStatus !== null &&
      savedStatus !== undefined &&
      Number(savedStatus) === Number(LeadStatusValues['active_funded'])
    );
  }

  private getStatusWarningType(
    currentStatus: number | null | undefined,
    newStatus: number | null | undefined,
  ): StatusWarningType {
    const curr = currentStatus != null ? Number(currentStatus) : null;
    const next = newStatus != null ? Number(newStatus) : null;

    if (curr === next) {
      return StatusWarningType.None;
    }

    // Case 1: Changing between Active Funded (9), Active Funded Grant (10), and Active Technical Assistance (11)
    if (
      curr !== null &&
      this.fundedOrTaStatuses.includes(curr) &&
      next !== null &&
      this.fundedOrTaStatuses.includes(next)
    ) {
      return StatusWarningType.BetweenFundedAndTA;
    }

    // Case 2: Changing from Active Client (7)
    if (curr === this.activeClientStatus) {
      // Active client to any other 3 active status is fine (no warning)
      if (next !== null && this.fundedOrTaStatuses.includes(next)) {
        return StatusWarningType.None;
      }
      // Active client to non-active status
      return StatusWarningType.ActiveToOther;
    }

    // Case 3: Changing from Active Funded (9), Active Funded Grant (10), or Active Technical Assistance (11) to any other status
    if (curr !== null && this.fundedOrTaStatuses.includes(curr)) {
      return StatusWarningType.ActiveToOther;
    }

    return StatusWarningType.None;
  }

  onDropdownChange(property: string, event: Event): void {
    const target = event.target as HTMLSelectElement;

    if (!this.leadAttributes) {
      return;
    }

    if (property === 'lead_status' && this.isActiveFund()) {
      target.value = String(this.leadAttributes['lead_status'] ?? 0);
      return;
    }

    const rawValue = target.value ? Number(target.value) : null;
    const newStatus = rawValue === 0 ? null : rawValue;

    if (property === 'lead_status') {
      const currentStatus =
        this.leadAttributes['lead_status'] != null
          ? Number(this.leadAttributes['lead_status'])
          : null;

      const warningType = this.getStatusWarningType(currentStatus, newStatus);

      if (warningType !== StatusWarningType.None) {
        this.pendingStatusChange = {
          property,
          newStatus,
          previousStatus: currentStatus,
          selectElement: target,
        };

        this.previousStatusLabel = this.getStatusLabel(currentStatus);
        this.newStatusLabel = this.getStatusLabel(newStatus);
        this.statusWarningHeading = 'Confirm Status Change';

        if (warningType === StatusWarningType.BetweenFundedAndTA) {
          const isCurrentFunded =
            currentStatus === Number(LeadStatusValues['active_funded']) ||
            currentStatus === Number(LeadStatusValues['active_funded_grant']);
          const isNewFunded =
            newStatus === Number(LeadStatusValues['active_funded']) ||
            newStatus === Number(LeadStatusValues['active_funded_grant']);

          if (isCurrentFunded && isNewFunded) {
            this.statusWarningSubtext =
              'Changing between funded loan and grant statuses will update the funding classification for this lead.';
          } else {
            this.statusWarningSubtext =
              'Changing between funded and technical assistance statuses will update the classification for this lead.';
          }
        } else {
          this.statusWarningSubtext =
            'Changing away from an active status will remove this lead from active tracking.';
        }

        this.showStatusChangePopup = true;
        return;
      }
    }

    this.applyDropdownChange(property, newStatus);
  }

  private applyDropdownChange(property: string, value: number | null): void {
    if (!this.leadAttributes) {
      return;
    }

    this.leadAttributes[property] = value;
  }

  onConfirmStatusChange(): void {
    if (this.pendingStatusChange) {
      const { property, newStatus } = this.pendingStatusChange;
      this.applyDropdownChange(property, newStatus);
    }
    this.pendingStatusChange = null;
    this.showStatusChangePopup = false;
  }

  onCancelStatusChange(): void {
    if (this.pendingStatusChange?.selectElement) {
      const prevVal = this.pendingStatusChange.previousStatus;
      this.pendingStatusChange.selectElement.value =
        prevVal !== null && prevVal !== undefined ? String(prevVal) : '0';
    }
    this.pendingStatusChange = null;
    this.showStatusChangePopup = false;
  }

  onInputChange(property: string, event: Event): void {
    const target = event.target as HTMLInputElement;

    if (!this.leadAttributes) {
      return;
    }

    const config = this.leadConfig.find((item) => item.property === property);

    if (config?.type === 'number') {
      const value = target.value;
      this.leadAttributes[property] = value === '' ? null : value;
    } else {
      this.leadAttributes[property] = target.value || null;
    }
  }

  isValid(): boolean {
    if (!this.recordId || !this.leadAttributes) {
      return false;
    }

    return true;
  }

  hasChanges(): boolean {
    return (
      JSON.stringify(this.leadAttributes) !==
      JSON.stringify(this.originalLeadAttributes)
    );
  }

  private getChangedAttributes(): Record<string, any> {
    const changedAttributes: Record<string, any> = {};

    if (!this.leadAttributes || !this.originalLeadAttributes) {
      return changedAttributes;
    }

    Object.keys(this.leadAttributes).forEach((key) => {
      if (
        JSON.stringify(this.leadAttributes![key]) !==
        JSON.stringify(this.originalLeadAttributes![key])
      ) {
        changedAttributes[key] = this.leadAttributes![key];
      }
    });

    return changedAttributes;
  }

  saveLeadAttributes(): void {
    if (this.leadAttributes) {
      const payload = this.getChangedAttributes();

      if (Object.keys(payload).length === 0) {
        return;
      }

      this.searchService
        .updateLeadAttributes(this.recordId, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.leadAttributes = res;
            this.originalLeadAttributes = { ...res };

            if (payload['lead_status'] !== undefined) {
              this.leadStatusUpdated.emit({
                uniqueId: this.recordId,
                newStatus: String(this.leadAttributes?.['lead_status'] ?? '0'),
              });
            }

            this.commonService.triggerToast({
              type: ToastType.Success,
              message: 'Lead Info updated successfully!',
            });
          },
          error: (err) => {
            this.leadAttributes = { ...this.originalLeadAttributes };

            this.commonService.triggerToast({
              type: ToastType.Error,
              message: err.error?.detail ?? 'Failed to update lead info!',
            });
          },
        });
    }
  }
}
